import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";
import GoogleConnect from "./GoogleConnect";
import SearchConsolePanel from "./SearchConsolePanel";
import AnalyticsPanel from "./AnalyticsPanel";

const RANGES = [[7, "7 days"], [28, "28 days"], [90, "3 months"]];
const RESULT = { connected: ["success", "Google connected"], denied: ["error", "Google access was not granted"], expired: ["error", "Connection expired, try again"], failed: ["error", "Google connection failed"] };

export default function GoogleInsights() {
  const [status, setStatus] = useState(null);
  const [days, setDays] = useState(28);
  const [src, setSrc] = useState("gsc");
  const [params, setParams] = useSearchParams();
  const load = useCallback(() => api.get("/admin/google/status").then((r) => setStatus(r.data)).catch(() => setStatus(false)), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const g = params.get("google");
    if (!g || !RESULT[g] || !status) return;
    const [kind, msg] = g === "connected" && !status.connected ? RESULT.failed : RESULT[g];
    toast[kind](msg); params.delete("google"); setParams(params, { replace: true });
  }, [params, setParams, status]);

  if (status === null) return <Loader2 className="mt-6 animate-spin text-white/30" />;
  if (status === false) return <p className="mt-4 text-sm text-red-400">Could not load Google status.</p>;
  const pill = (on) => `px-3 py-1.5 rounded-full text-xs font-bold ${on ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]" : "bg-white/5 text-white/60 hover:text-white"}`;
  return (
    <section className="mt-5" data-testid="google-insights">
      <GoogleConnect status={status} onChange={load} />
      {status.connected && (
        <>
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
            <div className="flex gap-1.5">
              <button onClick={() => setSrc("gsc")} className={pill(src === "gsc")} data-testid="google-src-gsc">Search Console</button>
              <button onClick={() => setSrc("ga")} className={pill(src === "ga")} data-testid="google-src-ga">Analytics 4</button>
            </div>
            <div className="flex gap-1.5">{RANGES.map(([v, l]) => <button key={v} onClick={() => setDays(v)} className={pill(days === v)} data-testid={`google-range-${v}`}>{l}</button>)}</div>
          </div>
          {src === "gsc"
            ? (status.site_url ? <SearchConsolePanel key={status.site_url} days={days} /> : <p className="mt-4 text-sm text-white/50">Choose a Search Console property above.</p>)
            : (status.property_id ? <AnalyticsPanel key={status.property_id} days={days} /> : <p className="mt-4 text-sm text-white/50">Choose a Google Analytics 4 property above.</p>)}
        </>
      )}
    </section>
  );
}
